import type { Schedule, Screen, ScreenGroup } from '@ghostscreen/contracts';
import { i18n } from '../../i18n/i18n';
import { windowsOverlapOnDay } from './scheduleWindow';

export type ScheduleConflict =
  | {
      kind: 'overlap';
      id: string;
      winnerName: string;
      loserName: string;
      winnerPriority: number;
      detail: string;
    }
  | {
      kind: 'uncovered';
      id: string;
      screenName: string;
      detail: string;
    };

type GroupDetail = Pick<ScreenGroup, 'id' | 'name'> & { screenIds?: string[] };

/**
 * UI-facing conflict summaries. Devices still resolve by priority at runtime;
 * this only surfaces overlaps and uncovered displays for operators.
 */
export function findScheduleConflicts(input: {
  schedules: Schedule[];
  screens: Screen[];
  groups: GroupDetail[];
}): ScheduleConflict[] {
  const conflicts: ScheduleConflict[] = [];
  const { schedules, screens, groups } = input;
  const groupMembers = new Map(
    groups.map((group) => [group.id, new Set(group.screenIds ?? [])] as const),
  );

  for (let i = 0; i < schedules.length; i += 1) {
    for (let j = i + 1; j < schedules.length; j += 1) {
      const a = schedules[i]!;
      const b = schedules[j]!;
      if (!targetsInteract(a, b, groupMembers)) continue;
      if (!overlapAnyDay(a, b)) continue;

      const winner = a.priority === b.priority
        ? specificity(a) >= specificity(b)
          ? a
          : b
        : a.priority > b.priority
          ? a
          : b;
      const loser = winner.id === a.id ? b : a;
      const winnerName = winner.name?.trim() || i18n.t('schedule:untitledDaypart');
      const loserName = loser.name?.trim() || i18n.t('schedule:untitledDaypart');
      conflicts.push({
        kind: 'overlap',
        id: `${a.id}:${b.id}`,
        winnerName,
        loserName,
        winnerPriority: winner.priority,
        detail: i18n.t('schedule:conflicts.overlapDetail', {
          winner: winnerName,
          loser: loserName,
          priority: winner.priority,
        }),
      });
    }
  }

  const covered = new Set<string>();
  for (const schedule of schedules) {
    if (schedule.screenId) covered.add(schedule.screenId);
    if (schedule.screenGroupId) {
      for (const screenId of groupMembers.get(schedule.screenGroupId) ?? []) {
        covered.add(screenId);
      }
    }
  }

  for (const screen of screens) {
    if (covered.has(screen.id)) continue;
    conflicts.push({
      kind: 'uncovered',
      id: `uncovered:${screen.id}`,
      screenName: screen.name,
      detail: i18n.t('schedule:conflicts.uncoveredDetail', { screen: screen.name }),
    });
  }

  return conflicts.slice(0, 8);
}

function specificity(schedule: Schedule): number {
  return schedule.screenId ? 1 : 0;
}

function targetsInteract(
  a: Schedule,
  b: Schedule,
  groupMembers: Map<string, Set<string>>,
): boolean {
  if (a.screenId && b.screenId) return a.screenId === b.screenId;
  if (a.screenGroupId && b.screenGroupId) return a.screenGroupId === b.screenGroupId;
  if (a.screenId && b.screenGroupId) {
    return groupMembers.get(b.screenGroupId)?.has(a.screenId) ?? false;
  }
  if (b.screenId && a.screenGroupId) {
    return groupMembers.get(a.screenGroupId)?.has(b.screenId) ?? false;
  }
  return false;
}

function overlapAnyDay(a: Schedule, b: Schedule): boolean {
  for (let day = 0; day < 7; day += 1) {
    if (windowsOverlapOnDay(a, b, day)) return true;
  }
  return false;
}

export function countCoveredScreens(input: {
  schedules: Schedule[];
  screens: Screen[];
  groups: GroupDetail[];
}): number {
  const covered = new Set<string>();
  const groupMembers = new Map(
    input.groups.map((group) => [group.id, new Set(group.screenIds ?? [])] as const),
  );
  for (const schedule of input.schedules) {
    if (schedule.screenId) covered.add(schedule.screenId);
    if (schedule.screenGroupId) {
      for (const screenId of groupMembers.get(schedule.screenGroupId) ?? []) {
        covered.add(screenId);
      }
    }
  }
  return [...covered].filter((id) => input.screens.some((screen) => screen.id === id)).length;
}
